import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Check, Loader2, ExternalLink } from 'lucide-react';

/**
 * "Save to Documents" button under every Nano Banana image. One click saves
 * the image to the workspace's "AI Images" folder in the Documents Hub so
 * the user can search/find it later. Stores the prompt as searchable
 * metadata. Once saved, the button shows a "Saved" checkmark (idempotent
 * per render).
 *
 * Extracted from pages/AIHub.jsx.
 */
export function SaveImageButton({ imageUrl, imageFilename, imagePrompt, onSave, attachmentIndex }) {
  const [saving, setSaving] = useState(false);
  const [savedRef, setSavedRef] = useState(null); // {documentId, folderId} after a successful save
  const navigate = useNavigate();

  const handleSave = async () => {
    if (saving || savedRef) return;
    setSaving(true);
    try {
      const result = await onSave({ imageUrl, imageFilename, imagePrompt });
      if (result) setSavedRef(result);
    } finally {
      setSaving(false);
    }
  };

  const handleView = () => {
    if (!savedRef) return;
    const params = new URLSearchParams({ space: 'firm' });
    if (savedRef.folderId) params.set('folder', savedRef.folderId);
    if (savedRef.documentId) params.set('doc', savedRef.documentId);
    navigate(`/documents?${params.toString()}`);
  };

  if (savedRef) {
    return (
      <div
        className="inline-flex items-center gap-1.5"
        data-testid={`ai-hub-save-image-saved-${attachmentIndex}`}
      >
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-950/30 text-xs font-medium text-emerald-700 dark:text-emerald-300">
          <Check className="w-3.5 h-3.5" />
          Saved
        </span>
        <button
          type="button"
          onClick={handleView}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800/50 bg-white hover:bg-emerald-50 dark:bg-zinc-900/40 dark:hover:bg-emerald-950/30 text-xs font-medium text-emerald-700 dark:text-emerald-300 transition-colors"
          data-testid={`ai-hub-save-image-view-${attachmentIndex}`}
          title="Open this image in the Documents Hub"
        >
          View in Documents
          <ExternalLink className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleSave}
      disabled={saving}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-zinc-200 dark:border-white/10 bg-white/50 dark:bg-zinc-900/40 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-xs font-medium text-zinc-700 dark:text-zinc-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid={`ai-hub-save-image-btn-${attachmentIndex}`}
      title="Save this image to your Documents Hub"
    >
      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-500" /> : <Save className="w-3.5 h-3.5 text-emerald-500" />}
      Save
    </button>
  );
}

export default SaveImageButton;
